/**
 * レポートデータ更新（汎用タスク画面）向け REST 呼び出し（IF02095 / IF02096 / IF02097 + create_data / fix_data / fix_cancel_data）。 パスは
 * api-patterns に従い PathInfoList.Resource のみで組み立てる。法人番号は API の corporation_resources と同様に Redux から解決する（SR-079）。
 */
import { errorLog } from '../../../../common/logger/Logger'
import { restApiKey, viewId } from '../../../../_constants/Code'
import { PathInfoList } from '../../../../_constants/PathInfoList'
import { httpGet, httpPost } from '../../../../services/api/AmplifyApi'

import {
  AllChrofyColumnsResponseSchema,
  ConversionTableListResponseSchema,
  GenericMappingSettingResponseSchema,
  JudgmentRuleResponseSchema,
  normalizeGenericTaskScreenResponse,
  parseLooseApiResponseBody
} from './types'

/** @typedef {import('./types').GenericTaskScreenNormalized} GenericTaskScreenNormalized */

/**
 * API Gateway の pathParameters.corporation_resources と同じ値（法人番号）を Redux から解決する（SR-079）。
 *
 * @param {{ getState?: () => unknown }} store
 * @returns {{ corporationNumber: string }}
 */
function _getCorporationResources(store) {
  const full = store.getState?.() ?? {}
  const session = full.mainStore ?? full
  const corporationNumber = String(session?.company?.corporationNumber ?? '').trim()
  return { corporationNumber }
}

/**
 * IF 呼び出しと同じ規則で Redux から法人番号を取得する（SR-079）。UI（確定解除キー等）でも API と同一ソースに揃える。
 *
 * @param {{ getState?: () => unknown }} store
 * @returns {string}
 */
export function getCorporationNumberFromStore(store) {
  return _getCorporationResources(store).corporationNumber
}

/**
 * IF02095: 汎用タスク画面の集約取得。クエリは data_create_task_key のみ（SR-003, SR-058）
 *
 * @param {{ getState?: () => unknown }} store
 * @param {string | number} dataCreateTaskKey
 * @returns {Promise<GenericTaskScreenNormalized>}
 */
export async function fetchGenericTaskScreen(store, dataCreateTaskKey) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const init = {
      headers: {},
      queryStringParameters: {
        data_create_task_key: Number(dataCreateTaskKey)
      }
    }
    const raw = await httpGet(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getGenericTaskScreen(corporationNumber),
      init
    )
    return normalizeGenericTaskScreenResponse(raw)
  } catch (error) {
    errorLog('E000001', '', viewId.analysisData, 'fetchGenericTaskScreen', error)
    throw error
  }
}

/**
 * IF02096: マッピング一括コピー（SR-060）
 *
 * @param {{ getState?: () => unknown }} store
 * @param {string | number} dataCreateTaskKey
 * @returns {Promise<unknown>}
 */
export async function postCopyPreviousMappingBulk(store, dataCreateTaskKey) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const body = { data_create_task_key: Number(dataCreateTaskKey) }
    const init = { headers: {}, body }
    const raw = await httpPost(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.copyPreviousMappingBulk(corporationNumber),
      init
    )
    return parseLooseApiResponseBody(raw)
  } catch (error) {
    errorLog('E000001', '', viewId.analysisData, 'postCopyPreviousMappingBulk', error)
    throw error
  }
}

/**
 * IF02097: 汎用データ検証一括開始（SR-063）
 *
 * @param {{ getState?: () => unknown }} store
 * @param {string | number} dataCreateTaskKey
 * @returns {Promise<unknown>}
 */
export async function postStartGenericDataValidationBulk(store, dataCreateTaskKey) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const body = { data_create_task_key: Number(dataCreateTaskKey) }
    const init = { headers: {}, body }
    const raw = await httpPost(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.startGenericDataValidationBulk(corporationNumber),
      init
    )
    return parseLooseApiResponseBody(raw)
  } catch (error) {
    errorLog('E000001', '', viewId.analysisData, 'postStartGenericDataValidationBulk', error)
    throw error
  }
}

/**
 * Create_data（データ更新）。FileUtils.dataCreate と同一ペイロード（SR-066）
 *
 * @param {{ getState?: () => unknown }} store
 * @param {{ userSub: string; periodKind: string; tableKey: unknown }} params
 * @returns {Promise<unknown>}
 */
export async function postCreateData(store, params) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const { userSub, periodKind, tableKey } = params
    const init = {
      headers: {},
      body: {
        request: {
          user_sub: userSub,
          corporation_number: corporationNumber,
          period_kind: periodKind,
          table_key: tableKey
        }
      }
    }
    const raw = await httpPost(restApiKey.CHROFY_REST_API, PathInfoList.Resource.dataCreate(corporationNumber), init)
    return parseLooseApiResponseBody(raw)
  } catch (error) {
    const err = /** @type {{ response?: { status?: number; data?: { message?: unknown } } }} */ (error)
    const status = err.response?.status
    const message = err.response?.data?.message
    if (status === 400 && typeof message === 'string' && message !== '') {
      return { ok: false, message }
    }
    errorLog('E000001', '', viewId.analysisData, 'postCreateData', error)
    throw error
  }
}

/**
 * Fix_data（データ確定）（SR-069）
 *
 * @param {{ getState?: () => unknown }} store
 * @param {{ userSub: string; userName: string; key: unknown }} params
 * @returns {Promise<unknown>}
 */
export async function postFixData(store, params) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const { userSub, userName, key } = params
    const init = {
      headers: {},
      body: {
        request: {
          corporation_number: corporationNumber,
          user_sub: userSub,
          user_name: userName,
          key
        }
      }
    }
    const raw = await httpPost(restApiKey.CHROFY_REST_API, PathInfoList.Resource.dataFix(corporationNumber), init)
    return parseLooseApiResponseBody(raw)
  } catch (error) {
    errorLog('E000001', '', viewId.analysisData, 'postFixData', error)
    throw error
  }
}

/**
 * Fix_cancel_data（データ確定解除）（SR-072）
 *
 * @param {{ getState?: () => unknown }} store
 * @param {{
 *   operationUserSub: string
 *   operationUserName: string
 *   permitUserName: string
 *   permitDate: string
 *   key: unknown
 * }} params
 * @returns {Promise<unknown>}
 */
export async function postFixCancelData(store, params) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const { operationUserSub, operationUserName, permitUserName, permitDate, key } = params
    const init = {
      headers: {},
      body: {
        request: {
          corporation_number: corporationNumber,
          operation_user_sub: operationUserSub,
          operation_user_name: operationUserName,
          permit_user_name: permitUserName,
          permit_date: permitDate,
          key
        }
      }
    }
    const raw = await httpPost(restApiKey.CHROFY_REST_API, PathInfoList.Resource.dataFixCancel(corporationNumber), init)
    return parseLooseApiResponseBody(raw)
  } catch (error) {
    errorLog('E000001', '', viewId.analysisData, 'postFixCancelData', error)
    throw error
  }
}

/**
 * IF02090: 全 CHROFY 項目一括取得（マッピング設定 Excel ダウンロード用）。
 *
 * 画面初期表示時に 1 回だけ呼び、項目名・データ型をストアへ保持する。 ダウンロード時に再取得しないことで、ボタン押下時のリクエストを最小化する（実装計画 5.1）。
 *
 * @param {{ getState?: () => unknown }} store
 * @returns {Promise<import('./types').ChrofyColumnGroup[]>}
 */
export async function fetchAllChrofyColumns(store) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const init = { headers: {}, queryStringParameters: {} }
    const raw = await httpGet(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getAllChrofyColumns(corporationNumber),
      init
    )
    return AllChrofyColumnsResponseSchema.parse(Array.isArray(raw) ? raw : [])
  } catch (error) {
    errorLog('E000001', '', viewId.analysisData, 'fetchAllChrofyColumns', error)
    throw error
  }
}

/**
 * IF02085: 汎用マッピング設定取得（タスク全体）。
 *
 * `generic_task_code` を**指定しない**ことで、検索キー前方一致が `{DataCreateTaskKey}#Mapping#` となり タスク全体のマッピングが mapping_info 付きで返る（実装計画
 * 4.4）。
 *
 * @param {{ getState?: () => unknown }} store
 * @param {string | number} dataCreateTaskKey
 * @returns {Promise<import('./types').GenericMappingSettingItem[]>}
 */
export async function fetchAllMappingSettings(store, dataCreateTaskKey) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const init = {
      headers: {},
      queryStringParameters: { data_create_task_key: Number(dataCreateTaskKey) }
    }
    const raw = await httpGet(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getGenericTaskSetting(corporationNumber),
      init
    )
    return GenericMappingSettingResponseSchema.parse(Array.isArray(raw) ? raw : [])
  } catch (error) {
    errorLog('E000001', '', viewId.analysisData, 'fetchAllMappingSettings', error)
    throw error
  }
}

/**
 * IF02092: 判定ルール取得（法人単位）。
 *
 * リクエストは法人番号のみで、1 リクエストで全ルールが返る。 条件式は BE の `revert_condition_format` により `[分類.項目]` 形式へ 逆変換済みのため、FE 側で AST
 * を解析する必要はない（実装計画 6.4.3）。
 *
 * @param {{ getState?: () => unknown }} store
 * @returns {Promise<import('./types').JudgmentRuleItem[]>}
 */
export async function fetchJudgmentRules(store) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const init = { headers: {}, queryStringParameters: {} }
    const raw = await httpGet(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getJudgmentRule(corporationNumber),
      init
    )
    return JudgmentRuleResponseSchema.parse(Array.isArray(raw) ? raw : [])
  } catch (error) {
    errorLog('E000001', '', viewId.analysisData, 'fetchJudgmentRules', error)
    throw error
  }
}

/**
 * IF02102: 変換表一覧取得（マッピング内容の変換表名解決用）。
 *
 * マッピングレコード（IF02085）は変換表を `conversion_table_key`（UUID）でしか 保持しないため、Excel に名称を出力するには本 API の一覧が必要（実装計画 6.4.2）。 レスポンスは
 * `conversion_table_list` にラップされる。
 *
 * @param {{ getState?: () => unknown }} store
 * @returns {Promise<import('./types').ConversionTableListItem[]>}
 */
export async function fetchConversionTableList(store) {
  try {
    const { corporationNumber } = _getCorporationResources(store)
    const raw = await httpGet(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.listConversionTables(corporationNumber),
      { headers: {} }
    )
    const list = /** @type {{ conversion_table_list?: unknown }} */ (raw ?? {})?.conversion_table_list
    return ConversionTableListResponseSchema.parse(Array.isArray(list) ? list : [])
  } catch (error) {
    errorLog('E000001', '', viewId.analysisData, 'fetchConversionTableList', error)
    throw error
  }
}
